import CtaArrow from "@/components/svg/CtaArrow";
import SubHeader from "@/components/SubHeader";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import {
  getTextComponents,
  getTitleComponents,
} from "@/helpers";

const ServicesCta = ({ field }) => {
  if (!isFilled.link(field)) {
    return null;
  }

  return (
    <PrismicNextLink prefetch={false}
      field={field}
      className={`cta cta-text bg-rouge text-beige-30 w-full md:w-fit`}
    >
      {field.text}
      <CtaArrow className="text-beige-30" />
    </PrismicNextLink>
  );
};

const BigTitleWatermark = ({ text, className }) => {
  if (!isFilled.keyText(text)) {
    return null;
  }

  return (
    <p
      aria-hidden
      className={`watermark-text ${className}`}
    >
      {text}
    </p>
  );
};

const servicesTitleComponents = {
  ...getTitleComponents({ color: "beige-30", highlightColor: "vert" }),
  heading2: ({ children }) => (
    <h2 className="h2-xl lg:h2-desktop-xl text-beige-30">
      {children}
    </h2>
  ),
  heading3: ({ children }) => (
    <h3 className="h2-xl lg:h2-desktop-xl text-beige-30">
      {children}
    </h3>
  ),
};

const quoteBoxComponents = ({ type = "" } = {}) => ({

  heading3: ({ children }) => (
    <h3 className="h4 text-vert-50">{children}</h3>
  ),
  heading4: ({ children }) => (
    <h4 className="h4 text-vert-50">{children}</h4>
  ),
  heading5: ({ children }) => (
    <h5 className="h4 text-vert-50">{children}</h5>
  ),
  paragraph: ({ children }) => (
    <p className={`${type !== 'lg' ? 'p-base' : 'p-xl md:p-xxl'} text-beige-30`}>
      {children}
    </p>
  )
});

const projectBriefTextComponents = {
  paragraph: ({ children }) => (
    <p className="text-lg leading-relaxed text-beige-30 md:p-lg">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hyperlink: ({ children, node }) => (
    <a
      href={node.data.url}
      className="text-beige-30 underline underline-offset-2"
    >
      {children}
    </a>
  ),
};

/**
 * @param {import("@prismicio/client").Content.ServicesSliceDefaultPrimaryServicesItem} item
 */
const ServiceCard = ({ item }) => {
  return (
    <div className="flex flex-1 flex-col gap-5 bg-bleu-50 p-[30px] md:p-10 rounded-[30px] md:rounded-[40px] ">
      <PrismicRichText field={item.titre} components={{
        heading3: ({ children }) =>
          <h3 className="h3 text-beige-30">{children}</h3>,
        heading4: ({ children }) =>
          <h3 className="h3 text-beige-30">{children}</h3>
      }} />
      <PrismicRichText field={item.text} components={{
        paragraph: ({ children }) => (
          <p className="p-xl md:p-xxl text-beige-30 ">
            {children}
          </p>
        ),
      }} />
    </div>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.ServicesSliceDefault>} props
 */
const ServicesDefault = ({ slice }) => {
  const {
    sub_header,
    title,
    sub_title,
    big_title,
    services_title,
    services,
    picture,
    quote,
    quote_sub_text,
    cta,
  } = slice.primary;

  const filledServices = Array.isArray(services)
    ? services.filter(
      (item) => isFilled.richText(item.titre) || isFilled.richText(item.text),
    )
    : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-bleu padding-x padding-y"
    >
      <div className="framing-lg flex flex-col gap-10 md:gap-15">
        <div className="flex flex-col gap-3 md:gap-4">
          {isFilled.keyText(sub_header) && <SubHeader text={sub_header} />}

          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={getTitleComponents({
                color: "beige-30",
                highlightColor: "bleu-50",
              })}
            />
          )}

          {isFilled.richText(sub_title) && (
            <div className="">
              <PrismicRichText field={sub_title} components={{
                heading3: ({ children }) => (
                  <h3 className="p-lg-medium md:p-xl-semibold text-blanc">{children}</h3>
                ),
              }} />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-10 md:gap-15">
          <BigTitleWatermark text={big_title} className="hidden xl:block" />
          <div className="flex flex-col gap-10 md:gap-15">
            {isFilled.richText(services_title) && (
              <div className="max-w-3xl">
                <PrismicRichText
                  field={services_title}
                  components={servicesTitleComponents}
                />
              </div>
            )}

            {filledServices.length > 0 && (
              <div className="flex flex-col gap-5 lg:flex-row md:gap-10">
                {filledServices.map((item, index) => (
                  <ServiceCard key={`service-${index}`} item={item} />
                ))}
              </div>
            )}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-15">
              {isFilled.image(picture) && (
                <div className="h-[280px] rounded-[20px] md:rounded-[30px] relative overflow-hidden w-full shrink-0 lg:h-auto lg:max-w-[450px] lg:flex-1 order-2 md:order-1">
                  <PrismicNextImage
                    field={picture}
                    className="h-full w-full lg:absolute lg:absolute-center  object-cover "
                    sizes="(max-width: 1024px) 100vw, 450px"
                  />
                </div>
              )}
              <div className="lg:py-10 flex flex-col gap-15 lg:flex-1 order-1 md:order-2">
                <div className=" flex flex-col gap-5">
                  {isFilled.richText(quote) && (
                    <div className="border-l border-vert-50 bg-bleu px-10 py-5">
                      <div className="flex flex-col gap-4">
                        <PrismicRichText field={quote} components={quoteBoxComponents({ type: 'lg' })} />
                      </div>
                    </div>
                  )}
                  {isFilled.richText(quote_sub_text) && (
                    <PrismicRichText
                      field={quote_sub_text}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="p-base md:p-lg text-beige-30 italic">{children}</p>
                        ),
                      }}
                    />
                  )}
                </div>
                <ServicesCta field={cta} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * @param {import("@prismicio/react").SliceComponentProps<import("@prismicio/client").Content.ServicesSliceProjectBrief>} props
 */
const ServicesProjectBrief = ({ slice }) => {
  const {
    big_title,
    services_title,
    sub_title,
    quote_text,
    quote,
    cta,
    picture,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno border-t border-bleu-50 bg-bleu padding-x padding-y"
    >
      <div className="framing-lg flex flex-col gap-10 md:gap-15">
        <BigTitleWatermark text={big_title} className="hidden xl:block" />

        {isFilled.richText(services_title) && (
          <div className="max-w-2xl">
            <PrismicRichText
              field={services_title}
              components={servicesTitleComponents}
            />
          </div>
        )}



        <div className="flex flex-col gap-10 lg:flex-row lg:items-stretch justify-between lg:gap-15">
          <div className="lg:py-10 flex flex-col gap-8 md:gap-10 lg:max-w-[750px] lg:flex-1">
            {isFilled.richText(quote_text) && (
              <PrismicRichText
                field={quote_text}
                components={projectBriefTextComponents}
              />
            )}

            {isFilled.image(picture) && (
              <div className="lg:hidden h-[280px] w-full shrink-0 lg:h-auto lg:max-w-[450px] lg:flex-1">
                <PrismicNextImage
                  field={picture}
                  className="h-full w-full rounded-[20px] object-cover md:rounded-[30px]"
                  sizes="(max-width: 1024px) 100vw, 450px"
                />
              </div>
            )}

            {isFilled.richText(quote) && (
              <div className="border-l border-vert-50 bg-bleu px-6 py-5 md:px-10 md:py-5">
                <PrismicRichText field={quote} components={quoteBoxComponents()} />
              </div>
            )}

            {isFilled.richText(sub_title) && (
              <PrismicRichText
                field={sub_title}
                components={getTextComponents({ color: "beige-30", variation: "lg" })}
              />
            )}

            <ServicesCta field={cta} />
          </div>

          {isFilled.image(picture) && (
            <div className="hidden relative rounded-[20px] md:rounded-[30px] overflow-hidden lg:block h-full w-full shrink-0 lg:h-auto lg:max-w-[450px] lg:flex-1">
              <PrismicNextImage
                field={picture}
                className="h-full w-full object-cover absolute absolute-center"
                sizes="(max-width: 1024px) 100vw, 450px"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.ServicesSlice} ServicesSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ServicesSlice>} ServicesProps
 * @type {import("react").FC<ServicesProps>}
 */
const Services = ({ slice }) => {
  if (slice.variation === "projectBrief") {
    return <ServicesProjectBrief slice={slice} />;
  }

  return <ServicesDefault slice={slice} />;
};

export default Services;
